import { useEffect, useRef, useState } from "react";
import { urlFor } from "../../services/sanity";
import type { Category } from "../../types";

interface CategoryFilterProps {
  categories: Category[];
  activeCategory: string | null; // null = "All"
  onSelectCategory: (slug: string | null) => void;
}

export default function CategoryFilter({
  categories,
  activeCategory,
  onSelectCategory,
}: CategoryFilterProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Funktion zum Ermitteln der Logo-URL aus Sanity emblem
  const getLogoUrl = (category: Category): string | null => {
    if (category.emblem) {
      return urlFor(category.emblem).width(80).height(80).url();
    }
    return null;
  };

  // Scroll-Position prüfen und Pfeil-Sichtbarkeit aktualisieren
  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    // Kann nach links scrollen, wenn nicht ganz am Anfang
    setCanScrollLeft(scrollLeft > 0);

    // Kann nach rechts scrollen, wenn nicht ganz am Ende
    // Kleine Toleranz von 1px für Rundungsfehler
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  };

  // Initial check und bei Kategorien-Änderungen
  useEffect(() => {
    checkScrollPosition();

    // ResizeObserver für dynamische Größenänderungen
    const container = scrollContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(checkScrollPosition);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [categories]);

  // Scroll-Handler
  const handleScroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 200; // Pixel pro Klick
    const targetScroll = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  };

  return (
    <div className="fixed top-12 left-0 right-0 z-40 bg-black/60 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="relative">
          {/* Left Divider Line */}
          {canScrollLeft && (
            <div className="absolute left-[10px] top-0 bottom-0 w-[1px] bg-white/20 z-40 pointer-events-none" />
          )}

          {/* Left Arrow */}
          {canScrollLeft && (
            <button
              onClick={() => handleScroll('left')}
              className="absolute -left-6 top-1/2 -translate-y-1/2 z-50 w-[34px] h-[100px] flex items-center justify-center bg-black/80 backdrop-blur-sm rounded-lg hover:bg-black/90 transition-all shadow-lg"
              aria-label="Nach links scrollen"
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            onScroll={checkScrollPosition}
            className="overflow-x-auto no-scrollbar"
          >
            <div className="flex items-center gap-3 w-max mx-auto">
            {/* All filter */}
            <button
              onClick={() => onSelectCategory(null)}
              className={`flex-shrink-0 flex flex-col items-center gap-0 px-4 py-2 text-sm font-medium transition-all ${
                activeCategory === null
                  ? "text-white opacity-100"
                  : "text-white/60 opacity-60 hover:opacity-80"
              }`}
            >
              <div className="w-16 h-16" aria-hidden="true" />
              <span className="whitespace-nowrap">Alle</span>
            </button>

            {/* Category filters */}
            {categories.map((category) => {
              const logoUrl = getLogoUrl(category);

              return (
                <button
                  key={category._id}
                  onClick={() => onSelectCategory(category.slug.current)}
                  className={`flex-shrink-0 flex flex-col items-center gap-0 px-4 py-2 text-sm font-medium transition-all ${
                    activeCategory === category.slug.current
                      ? "text-white opacity-100"
                      : "text-white/60 opacity-60 hover:opacity-80"
                  }`}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`${category.name} logo`}
                      className="w-16 h-16 object-contain"
                    />
                  ) : (
                    <div className="w-16 h-16" aria-hidden="true" />
                  )}
                  <span className="whitespace-nowrap">{category.name}</span>
                </button>
              );
            })}
            </div>
          </div>

          {/* Right Divider Line */}
          {canScrollRight && (
            <div className="absolute right-[10px] top-0 bottom-0 w-[1px] bg-white/20 z-40 pointer-events-none" />
          )}

          {/* Right Arrow */}
          {canScrollRight && (
            <button
              onClick={() => handleScroll('right')}
              className="absolute -right-6 top-1/2 -translate-y-1/2 z-50 w-[34px] h-[100px] flex items-center justify-center bg-black/80 backdrop-blur-sm rounded-lg hover:bg-black/90 transition-all shadow-lg"
              aria-label="Nach rechts scrollen"
            >
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
